import * as React from "react";
import { cn } from "./utils";

const buttonVariants = {
  default:
    "bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_4px_12px_-2px_hsla(var(--color-primary),0.3)]",
  primary:
    "bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_4px_12px_-2px_hsla(var(--color-primary),0.3)]",
  destructive:
    "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-[0_4px_12px_-2px_hsla(var(--color-destructive),0.3)]",
  outline:
    "border border-border/60 bg-white/5 hover:bg-white/10 hover:border-border text-foreground backdrop-blur-sm",
  secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
  ghost: "hover:bg-primary/5 hover:text-primary",
  link: "text-primary underline-offset-4 hover:underline",
};

const buttonSizes = {
  default: "h-10 px-5 py-2.5",
  sm: "h-9 rounded-xl px-4",
  lg: "h-12 rounded-[14px] px-8 text-base",
  icon: "h-10 w-10",
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof buttonVariants;
  size?: keyof typeof buttonSizes;
  asChild?: boolean;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "default",
      size = "default",
      asChild = false,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? "span" : "button";
    const isDisabled = disabled || loading;

    return (
      <Comp
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-xl text-sm font-semibold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50",
          buttonVariants[variant],
          buttonSizes[size],
          className,
        )}
        ref={ref}
        disabled={isDisabled}
        {...props}
        aria-busy={loading ? true : props["aria-busy"]}
      >
        {loading && (
          <svg
            className="h-4 w-4 animate-spin"
            viewBox="0 0 24 24"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        )}
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";
